import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
	Select,
	MenuItem,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Chip,
	Tooltip,
} from "@mui/material";
import { mdiPlus, mdiPencil, mdiTrashCanOutline, mdiRefresh, mdiMagnify, mdiClose, mdiPuzzle } from "@mdi/js";
import { clsx } from "@ismael1361/utils";
import { HandleError } from "shared/utils";
import { NotistackServices } from "shared/services";
import { McpLibraryServices, PublicMcpDTO, CreateMcpPayload, UpdateMcpPayload } from "shared/services/McpLibraryServices";

import { McpFormDialog, McpFormSubmitData } from "./components/McpFormDialog";

import styles from "./styles.module.scss";

// --- Component ---

export const McpLibraryPage: React.FC = () => {
	const [mcps, setMcps] = useState<PublicMcpDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string>("ALL");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingMcp, setEditingMcp] = useState<PublicMcpDTO | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<PublicMcpDTO | null>(null);
	const time = useRef<NodeJS.Timeout>(undefined);

	const categories = React.useMemo(() => {
		const cats = new Set(mcps.map((m) => m.category));
		return Array.from(cats).sort();
	}, [mcps]);

	const filteredMcps = React.useMemo(() => {
		return mcps.filter((m) => {
			if (categoryFilter !== "ALL" && m.category !== categoryFilter) return false;
			if (searchQuery) {
				const q = searchQuery.toLowerCase();
				return m.name.toLowerCase().includes(q) || m.slug.toLowerCase().includes(q) || m.description?.toLowerCase().includes(q);
			}
			return true;
		});
	}, [mcps, categoryFilter, searchQuery]);

	const loadMcps = useCallback(() => {
		setLoading(true);
		clearTimeout(time.current);

		time.current = setTimeout(async () => {
			try {
				const data = await McpLibraryServices.listMcps();
				setMcps(data);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar MCPs");
			} finally {
				setLoading(false);
			}
		}, 300);
	}, []);

	useEffect(() => {
		loadMcps();
	}, [loadMcps]);

	// --- Dialog handlers ---

	const handleOpenCreate = () => {
		setEditingMcp(null);
		setDialogOpen(true);
	};

	const handleOpenEdit = (mcp: PublicMcpDTO) => {
		setEditingMcp(mcp);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditingMcp(null);
	};

	const handleSave = async (data: McpFormSubmitData) => {
		const createSlug = data.slug;

		setSaving(true);
		try {
			if (editingMcp) {
				const payload: UpdateMcpPayload = {
					name: data.name,
					category: data.category,
					description: data.description,
					system_prompt: data.systemPrompt,
					deliverable_types: data.deliverableTypes,
					icon_url: data.iconUrl,
					pricing_model: data.pricingModel,
					mcp_server_config: data.mcpServerConfig,
					tool_env_fields: data.toolEnvFields,
				};
				await McpLibraryServices.updateMcp(editingMcp.id, payload);
				NotistackServices.showSuccessMessage("MCP atualizado com sucesso!");
			} else {
				if (!createSlug) {
					NotistackServices.showErrorMessage("Slug é obrigatório para novos MCPs.");
					return;
				}

				const payload: CreateMcpPayload = {
					slug: createSlug,
					name: data.name,
					category: data.category,
					description: data.description,
					system_prompt: data.systemPrompt,
					deliverable_types: data.deliverableTypes,
					icon_url: data.iconUrl,
					pricing_model: data.pricingModel,
					mcp_server_config: data.mcpServerConfig,
					tool_env_fields: data.toolEnvFields,
				};
				await McpLibraryServices.createMcp(payload);
				NotistackServices.showSuccessMessage("MCP criado com sucesso!");
			}
			handleCloseDialog();
			loadMcps();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao salvar MCP.");
		} finally {
			setSaving(false);
		}
	};

	const handleDeactivate = async () => {
		if (!confirmDelete) return;

		setSaving(true);
		try {
			await McpLibraryServices.deleteMcp(confirmDelete.id);
			NotistackServices.showSuccessMessage("MCP desativado com sucesso!");
			setConfirmDelete(null);
			loadMcps();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao desativar MCP.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<div className={styles.container}>
				{/* Header */}
				<div className={styles.pageHeader}>
					<div>
						<h2>Biblioteca de MCPs</h2>
						<p>Crie, edite e gerencie os MCPs disponíveis na plataforma.</p>
					</div>
					<div className={styles.headerActions}>
						<button
							className={clsx(styles.btn, styles.btnSecondary)}
							onClick={loadMcps}
							disabled={loading}
						>
							<Icon
								path={mdiRefresh}
								size={0.7}
							/>
							Recarregar
						</button>
						<button
							className={clsx(styles.btn, styles.btnPrimary)}
							onClick={handleOpenCreate}
						>
							<Icon
								path={mdiPlus}
								size={0.7}
							/>
							Novo MCP
						</button>
					</div>
				</div>

				{/* Filters */}
				<div className={styles.filtersToolbar}>
					<div className={styles.filterGroup}>
						<span className={styles.label}>Categoria:</span>
						<Select
							className={styles.select}
							variant="standard"
							disableUnderline
							value={categoryFilter}
							onChange={(e) => setCategoryFilter(e.target.value)}
							disabled={loading}
						>
							<MenuItem value="ALL">Todas</MenuItem>
							{categories.map((cat) => (
								<MenuItem
									key={cat}
									value={cat}
								>
									{cat}
								</MenuItem>
							))}
						</Select>
					</div>

					<div className={styles.searchGroup}>
						<Icon
							path={mdiMagnify}
							size={0.65}
							className={styles.searchIcon}
						/>
						<input
							className={styles.searchInput}
							placeholder="Buscar por nome, slug..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							disabled={loading}
						/>
					</div>

					<div className={styles.templateCount}>
						<span>
							{filteredMcps.length} MCP{filteredMcps.length !== 1 && "s"}
						</span>
					</div>
				</div>

				{/* Content */}
				<div className={styles.content}>
					{loading ? (
						<div className={styles.loadingState}>
							<CircularProgress
								color="inherit"
								size={24}
							/>
							<span>Carregando MCPs...</span>
						</div>
					) : filteredMcps.length === 0 ? (
						<div className={styles.emptyState}>
							<Icon
								path={mdiPuzzle}
								size={2}
							/>
							<p>Nenhum MCP encontrado.</p>
						</div>
					) : (
						<div className={styles.grid}>
							{filteredMcps.map((mcp) => (
								<div
									key={mcp.id}
									className={clsx(styles.card, !mcp.isActive && styles.inactive)}
								>
									<div className={styles.cardHeader}>
										<div className={styles.cardIcon}>
											{mcp.iconUrl ? (
												<img
													src={mcp.iconUrl}
													alt={mcp.name}
												/>
											) : (
												<Icon
													path={mdiPuzzle}
													size={1}
												/>
											)}
										</div>
										<div className={styles.cardInfo}>
											<h3>{mcp.name}</h3>
											<span className={styles.slug}>{mcp.slug}</span>
										</div>
										<div className={styles.cardActions}>
											<Tooltip title="Editar">
												<IconButton
													size="small"
													onClick={() => handleOpenEdit(mcp)}
												>
													<Icon
														path={mdiPencil}
														size={0.7}
													/>
												</IconButton>
											</Tooltip>
											{mcp.isActive && (
												<Tooltip title="Desativar">
													<IconButton
														size="small"
														onClick={() => setConfirmDelete(mcp)}
													>
														<Icon
															path={mdiTrashCanOutline}
															size={0.7}
														/>
													</IconButton>
												</Tooltip>
											)}
										</div>
									</div>

									<div className={styles.cardBody}>
										{mcp.description && <p className={styles.description}>{mcp.description}</p>}
										<div className={styles.meta}>
											<Chip
												label={mcp.category}
												size="small"
												variant="outlined"
											/>
											{!mcp.isActive && (
												<Chip
													label="Inativo"
													size="small"
													color="error"
													variant="outlined"
												/>
											)}
											{mcp.deliverableTypes?.length > 0 && (
												<Chip
													label={`${mcp.deliverableTypes.length} entregável(is)`}
													size="small"
													variant="outlined"
												/>
											)}
											{Object.keys(mcp.mcpServerConfig ?? {}).length > 0 && (
												<Chip
													label="Server Config"
													size="small"
													color="info"
													variant="outlined"
												/>
											)}
										</div>
									</div>

									<div className={styles.cardFooter}>
										<span>Criado em: {new Date(mcp.createdAt).toLocaleDateString("pt-BR")}</span>
										<span>Atualizado: {new Date(mcp.updatedAt).toLocaleDateString("pt-BR")}</span>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<McpFormDialog
				open={dialogOpen}
				saving={saving}
				editingMcp={editingMcp}
				onClose={handleCloseDialog}
				onValidationError={(message) => NotistackServices.showErrorMessage(message)}
				onSubmit={handleSave}
			/>

			{/* Confirm Deactivation Dialog */}
			<Dialog
				open={!!confirmDelete}
				onClose={() => setConfirmDelete(null)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ className: styles.dialogPaper }}
			>
				<DialogTitle className={styles.dialogTitle}>
					<span>Desativar MCP</span>
					<IconButton
						size="small"
						onClick={() => setConfirmDelete(null)}
					>
						<Icon
							path={mdiClose}
							size={0.8}
						/>
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<p className={styles.confirmText}>
						Tem certeza que deseja desativar o MCP <strong>{confirmDelete?.name}</strong>?
					</p>
					<p className={styles.confirmHint}>O MCP não será excluído permanentemente, apenas marcado como inativo.</p>
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<Button
						onClick={() => setConfirmDelete(null)}
						disabled={saving}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleDeactivate}
						disabled={saving}
					>
						{saving ? (
							<CircularProgress
								size={18}
								color="inherit"
							/>
						) : (
							"Desativar"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
